<script setup lang="ts">
/**
 * Avis sur l'association — charte « apple » (Karim 2026-10-18) : grandes
 * cartes #f5f5f7, citation en corps de lecture, auteur en bas. Défilement
 * continu seulement si les cartes débordent de l'écran (pause au survol).
 */
import { TITRE_SECTION_APPLE } from "~/utils/titresApple";

interface Testimonial {
  id: number;
  author_name: string;
  role: string | null;
  content: string;
  rating: number | null;
  image: string | null;
}

const props = defineProps<{
  testimonials: Testimonial[];
  assetsUrl: string;
}>();

// Nommé `traduire` : `t` est déjà pris par la variable de boucle `v-for="(t,
// idx) in displayedTestimonials"` ci-dessous (chaque témoignage).
const { t: traduire } = useI18n();

const shouldScroll = ref(false);
const testimonialsScrollRef = ref<HTMLElement | null>(null);

const displayedTestimonials = computed(() => {
  // Duplicate la liste si le carousel doit défiler (sinon une seule copie suffit)
  return shouldScroll.value ? [...props.testimonials, ...props.testimonials] : props.testimonials;
});

function checkOverflow() {
  if (props.testimonials.length <= 1) {
    shouldScroll.value = false;
    return;
  }
  // Estimation : chaque carte ~360px + gap 20px
  const totalWidth = props.testimonials.length * (360 + 20);
  shouldScroll.value = totalWidth > window.innerWidth;
}

onMounted(() => {
  nextTick(checkOverflow);
  window.addEventListener("resize", checkOverflow);
  onUnmounted(() => window.removeEventListener("resize", checkOverflow));
});
</script>

<template>
  <section id="section-testimonials" class="scroll-mt-24">
    <div class="pa-reveal mx-auto mb-10 max-w-[1120px] px-4 sm:px-8">
      <h2 class="text-[32px] text-[#1d1d1f] sm:text-[40px]" :style="TITRE_SECTION_APPLE">{{ traduire("association.temoignages.titre") }}</h2>
      <p class="mt-3 max-w-[680px] text-[17px] font-medium leading-[1.5] text-[#6e6e73]">{{ traduire("association.temoignages.texte") }}</p>
    </div>
    <div class="testimonials-track pa-reveal" :class="{ 'testimonials-track--scroll': shouldScroll }">
      <div ref="testimonialsScrollRef" class="testimonials-scroll">
        <template v-for="(t, idx) in displayedTestimonials" :key="`t-${idx}`">
          <figure class="flex w-[280px] shrink-0 flex-col rounded-[28px] bg-[#f5f5f7] p-7 sm:w-[360px]">
            <div v-if="t.rating" class="mb-4 flex gap-0.5" :aria-label="`${t.rating}/5`">
              <!-- Étoile PLEINE = icône pleine : une icône CSS (masque) ne se
                   remplit pas avec `fill`, les 5 étoiles restaient en contour. -->
              <UIcon v-for="i in 5" :key="i" :name="i <= t.rating ? 'i-heroicons-star-solid' : 'i-lucide-star'" class="size-4" :class="i <= t.rating ? 'text-[#1d1d1f]' : 'text-[#c7c7cc]'" />
            </div>
            <!-- eslint-disable vue/no-v-html -->
            <!-- Reason: t.content est sanitizé côté backend Donoor -->
            <blockquote class="mb-6 line-clamp-5 flex-1 text-[17px] leading-[1.55] text-[#1d1d1f]" v-html="t.content" />
            <!-- eslint-enable vue/no-v-html -->
            <figcaption class="flex items-center gap-3">
              <img
                v-if="t.image"
                :src="`${assetsUrl}/${t.image}?width=88&height=88&fit=cover`"
                :alt="t.author_name"
                class="size-11 shrink-0 rounded-full object-cover"
              />
              <div v-else class="flex size-11 shrink-0 items-center justify-center rounded-full bg-white text-[15px] font-bold text-[#1d1d1f]">
                {{ t.author_name.charAt(0).toUpperCase() }}
              </div>
              <div class="min-w-0">
                <p class="truncate text-[15px] font-semibold text-[#1d1d1f]">{{ t.author_name }}</p>
                <p v-if="t.role" class="truncate text-[13px] text-[#6e6e73]">{{ t.role }}</p>
              </div>
            </figcaption>
          </figure>
        </template>
      </div>
    </div>
  </section>
</template>

<style scoped>
.testimonials-track {
  padding-bottom: 0.5rem;
}

.testimonials-scroll {
  display: flex;
  gap: 1.25rem;
  max-width: 70rem;
  margin: 0 auto;
  padding: 0 1rem;
  flex-wrap: wrap;
  justify-content: center;
}

.testimonials-track--scroll {
  overflow: hidden;
  mask-image: linear-gradient(to right, transparent, black 3%, black 97%, transparent);
  -webkit-mask-image: linear-gradient(to right, transparent, black 3%, black 97%, transparent);
}

.testimonials-track--scroll .testimonials-scroll {
  flex-wrap: nowrap;
  justify-content: flex-start;
  max-width: none;
  width: max-content;
  animation: scroll-testimonials 45s linear infinite;
  padding: 0;
}

.testimonials-track--scroll .testimonials-scroll:hover {
  animation-play-state: paused;
}

@keyframes scroll-testimonials {
  0% { transform: translateX(0); }
  100% { transform: translateX(-50%); }
}

@media (prefers-reduced-motion: reduce) {
  .testimonials-track--scroll .testimonials-scroll {
    animation: none;
  }
}
</style>
